import * as React from 'react';
import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import { LockKey as LockKeyIcon } from '@phosphor-icons/react/dist/ssr/LockKey';
import { Eye as EyeIcon } from '@phosphor-icons/react/dist/ssr/Eye';
import { EyeSlash as EyeSlashIcon } from '@phosphor-icons/react/dist/ssr/EyeSlash';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useUser } from '@/hooks/v2/use-user';
import axios from '@/lib/v2/axios';
import { toast } from '@/components/core/v2/toaster';

const validationSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .required('Current password is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    )
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

export function UserSecuritySettings(): React.JSX.Element {
  const { user } = useUser();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        console.log('Submitting password change...', { values });
        
        const response = await axios.put('/api/v2/auth/password', {
          current_password: values.currentPassword,
          password: values.newPassword,
          password_confirmation: values.confirmPassword,
        });
        
        console.log('Password change response:', response);
        
        // Handle successful response (200)
        if (response.status === 200) {
          console.log('Password updated successfully, showing success toast');
          toast.success('Password updated successfully');
          resetForm();
        }
      } catch (error: any) {
        console.error('Password update error:', error);
        
        // Handle different error scenarios
        if (error.response) {
          const { status, data } = error.response;
          console.log('Error response:', { status, data });
          
          switch (status) {
            case 400:
              // Bad request - validation errors or business logic errors
              if (data?.message === 'New password must be different from your current password') {
                console.log('Showing password same error toast');
                toast.error('New password must be different from your current password');
              } else if (data?.message === 'Current password is incorrect') {
                console.log('Showing current password error toast');
                toast.error('Current password is incorrect');
              } else {
                const message = data?.message || 'Invalid request';
                console.log('Showing generic 400 error toast:', message);
                toast.error(message);
              }
              break;
            case 401:
              // Unauthorized
              console.log('Showing unauthorized error toast');
              toast.error('You are not authorized to perform this action');
              break;
            case 422:
              // Validation errors
              console.log('Showing validation error toast');
              toast.error('Please check your input and try again');
              break;
            case 500:
              // Server error
              console.log('Showing server error toast');
              toast.error('Server error. Please try again later');
              break;
            default:
              // Other error statuses
              const defaultMessage = data?.message || 'Failed to update password';
              console.log('Showing default error toast:', defaultMessage);
              toast.error(defaultMessage);
          }
        } else if (error.request) {
          // Network error
          console.log('Showing network error toast');
          toast.error('Network error. Please check your connection');
        } else {
          // Other errors
          console.log('Showing unexpected error toast');
          toast.error('An unexpected error occurred');
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const togglePasswordVisibility = useCallback((field: string) => {
    switch (field) {
      case 'new':
        setShowNewPassword(prev => !prev);
        break;
      case 'confirm':
        setShowConfirmPassword(prev => !prev);
        break;
    }
  }, []);

  return (
    <Stack spacing={3}>
      <Card>
        <CardHeader
          avatar={<LockKeyIcon size={24} />}
          subheader="Update your account password to keep your account secure"
          title="Change Password"
        />
        <CardContent>
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={3}>
              <Alert severity="info">
                Your new password must be at least 8 characters long and contain uppercase, lowercase, and numeric characters.
              </Alert>
              
              <FormControl size="small" variant="outlined">
                <InputLabel htmlFor="current-password">Current Password</InputLabel>
                <OutlinedInput
                  error={formik.touched.currentPassword ? Boolean(formik.errors.currentPassword) : undefined}
                  id="current-password"
                  label="Current Password"
                  name="currentPassword"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="password"
                  value={formik.values.currentPassword}
                />
                {formik.touched.currentPassword && formik.errors.currentPassword ? <FormHelperText error>{formik.errors.currentPassword}</FormHelperText> : null}
              </FormControl>
              
              <FormControl size="small" variant="outlined">
                <InputLabel htmlFor="new-password">New Password</InputLabel>
                <OutlinedInput
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showNewPassword ? 'hide password' : 'show password'}
                        edge="end"
                        onClick={() => { togglePasswordVisibility('new'); }}
                        size="small"
                      >
                        {showNewPassword ? <EyeSlashIcon size={16} /> : <EyeIcon size={16} />}
                      </IconButton>
                    </InputAdornment>
                  }
                  error={formik.touched.newPassword ? Boolean(formik.errors.newPassword) : undefined}
                  id="new-password"
                  label="New Password"
                  name="newPassword"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type={showNewPassword ? 'text' : 'password'}
                  value={formik.values.newPassword}
                />
                {formik.touched.newPassword && formik.errors.newPassword ? <FormHelperText error>{formik.errors.newPassword}</FormHelperText> : null}
              </FormControl>

              <FormControl size="small" variant="outlined">
                <InputLabel htmlFor="confirm-password">Confirm New Password</InputLabel>
                <OutlinedInput
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showConfirmPassword ? 'hide password' : 'show password'}
                        edge="end"
                        onClick={() => { togglePasswordVisibility('confirm'); }}
                        size="small"
                      >
                        {showConfirmPassword ? <EyeSlashIcon size={16} /> : <EyeIcon size={16} />}
                      </IconButton>
                    </InputAdornment>
                  }
                  error={formik.touched.confirmPassword ? Boolean(formik.errors.confirmPassword) : undefined}
                  id="confirm-password"
                  label="Confirm New Password"
                  name="confirmPassword"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formik.values.confirmPassword}
                />
                {formik.touched.confirmPassword && formik.errors.confirmPassword ? <FormHelperText error>{formik.errors.confirmPassword}</FormHelperText> : null}
              </FormControl>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  disabled={formik.isSubmitting}
                  sx={{ minWidth: 120 }}
                  type="submit"
                  variant="contained"
                >
                  {formik.isSubmitting ? 'Updating...' : 'Update Password'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Stack>
  );
}
